import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";

const SSO_ERROR_MESSAGES = {
  not_configured: "Microsoft sign-in isn't set up yet — contact your admin, or use a demo/gate account below.",
  domain_not_allowed: "Only @rampgroup.co.in Microsoft accounts can sign in this way. Use a demo/gate account below instead.",
  account_disabled: "This account has been disabled. Contact your admin.",
  exchange_failed: "Microsoft sign-in failed. Please try again.",
  missing_code: "Microsoft sign-in was cancelled. Please try again.",
  invalid_state: "Your sign-in session expired. Please try again.",
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ssoError = searchParams.get("sso_error");
    if (ssoError) {
      setError(SSO_ERROR_MESSAGES[ssoError] || "Microsoft sign-in failed. Please try again.");
      window.history.replaceState({}, "", "/login");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "security" ? "/kiosk" : user.role === "employee" ? "/pre-register" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 text-brand-navy">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1670689334799-cdc6777db8cc?crop=entropy&cs=srgb&fm=jpg&q=85)" }}
      >
        <div className="absolute inset-0 bg-brand-navy/80" />
        <div className="relative z-10 p-12 flex flex-col justify-end h-full">
          <img src="/brand/radish-logo-light.png" alt="Radish Technologies" className="h-10 w-auto self-start mb-8" data-testid="login-brand-logo" />
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-cyan mb-4">Visitor Management System</span>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3 text-white">
            Factory
            <br />
            Visitor Hub
          </h1>
          <p className="text-slate-300 max-w-md">Gate check-in, compliance briefings, and visitor audit — under one roof.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <img src="/brand/radish-logo.png" alt="Radish Technologies" className="h-9 w-auto mb-6 lg:hidden" data-testid="login-form-logo" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Sign In</span>
            <h2 className="text-2xl font-bold uppercase tracking-tight mt-2 text-brand-navy">Access Your Account</h2>
          </div>
          {error && (
            <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="login-error">
              {error}
            </p>
          )}
          <Button
            type="button"
            onClick={() => (window.location.href = `${API}/auth/microsoft/login`)}
            data-testid="microsoft-sso-login-button"
            className="w-full h-11 rounded-sm bg-white border border-slate-300 text-brand-navy hover:bg-slate-50 font-bold text-sm gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </Button>
          <p className="text-xs text-center text-slate-400 -mt-3">For Admin &amp; Host accounts — @rampgroup.co.in only</p>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-widest text-slate-400">Gate / Demo Login</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm bg-white border-slate-300 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm bg-white border-slate-300 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase tracking-wide text-sm"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
